import { useEffect, useRef, useState } from 'react'
import { Service } from '../service/terminal'
import { alpha, useTheme } from '@mui/material/styles'
import { TRANSITION_DURATION } from '../constants'
import useTerminal, { getService, registerService } from '../models/useTerminal'
import useServices from '../models/useServices'
import useNode from '../models/useNode'
import { getHost } from '../utilities/service'
import { SERVICE_DOMAIN } from '../constants'

type Status = 'connecting' | 'connected' | 'disconnected'

type UseTerminalServiceProps = {
  sessionId: string
}

export default function useTerminalService({ sessionId }: UseTerminalServiceProps) {
  const theme = useTheme()
  const session = useTerminal(state => state.sessions.find(s => s.id === sessionId))
  const serviceName = session?.serviceName
  const service = useServices(state => state.services.find(s => s.name === serviceName))
  const node = useNode(state => state.nodes.find(n => n.id === service?.nodeId))

  const containerRef = useRef<HTMLDivElement>(null)
  const termRootRef = useRef<HTMLDivElement>(null)
  const [terminalService, setTerminalService] = useState<Service | null>(() => getService(sessionId))

  const [status, setStatus] = useState<Status>(terminalService?.getStatus() ?? 'connecting')

  const host = service ? getHost(service) : serviceName ? `${serviceName.toLowerCase()}.${SERVICE_DOMAIN}` : undefined
  // The WebSocket is keyed on the service NAME and goes to the router's host; `host` is only shown.
  const socketName = service?.name ?? serviceName
  const title = service?.title || host || 'Terminal'

  // Initialize service
  useEffect(() => {
    if (!session || terminalService) return
    const existing = getService(sessionId)
    if (existing) {
      setTerminalService(existing)
      return
    }
    const svc = new Service({
      background: theme.palette.background.default,
      foreground: theme.palette.contrast.max,
      cursor: theme.palette.primary.main,
      selectionBackground: alpha(theme.palette.primary.main, 0.25),
      selectionInactiveBackground: alpha(theme.palette.primary.main, 0.15),
    })
    registerService(sessionId, svc)
    setTerminalService(svc)
  }, [session, sessionId, terminalService, theme])

  // Attach/detach lifecycle
  useEffect(() => {
    const root = termRootRef.current
    if (!root || !terminalService) return

    terminalService.attachToElement(root, containerRef.current || undefined)

    const unsubscribe = terminalService.onStatusChange(setStatus)
    setStatus(terminalService.getStatus())

    return () => {
      unsubscribe()
      terminalService.detach()
    }
  }, [terminalService])

  // Fit on mode changes (after transition animation completes)
  useEffect(() => {
    if (!terminalService || session?.mode === 'minimized') return
    const timer = setTimeout(() => terminalService.scheduleFit(), TRANSITION_DURATION)
    return () => clearTimeout(timer)
  }, [session?.mode, terminalService])

  // Auto-connect
  useEffect(() => {
    if (!socketName || !terminalService) return
    if (terminalService.getStatus() !== 'connected') {
      terminalService.connect(socketName, title)
    }
  }, [socketName, title, terminalService])

  // Update theme
  useEffect(() => {
    if (!terminalService) return
    terminalService.setTheme({
      background: theme.palette.background.default,
      foreground: theme.palette.contrast.max,
      cursor: theme.palette.primary.main,
      selectionBackground: alpha(theme.palette.primary.main, 0.25),
      selectionInactiveBackground: alpha(theme.palette.primary.main, 0.15),
    })
  }, [theme, terminalService])

  const toggleConnection = () => {
    if (!terminalService || !socketName) return
    if (status === 'connected') {
      terminalService.disconnect()
      setStatus('disconnected')
    } else {
      terminalService.connect(socketName, title)
    }
  }

  return {
    service: terminalService,
    status,
    host,
    title,
    node,
    serviceName,
    containerRef,
    termRootRef,
    toggleConnection,
  }
}
